import { useMemo, useState } from "react";
import { BarChart3, MonitorPlay } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FilterPopover, type FilterField } from "@/components/waste/filter-popover";
import { BaseCoversDialog } from "@/components/dashboard/base-covers-dialog";
import { useOccupancyRangeCovers } from "@/hooks/use-occupancy";
import { DashboardKpis } from "@/components/dashboard/dashboard-kpis";
import { FoodSpendDialog } from "@/components/dashboard/food-spend-dialog";
import { KitchenDisplay } from "@/components/dashboard/kitchen-display";
import { PeriodControl } from "@/components/dashboard/period-control";
import { ProofWidget } from "@/components/dashboard/proof-widget";
import { PulseStrip } from "@/components/dashboard/pulse-strip";
import { WIDGET_REGISTRY } from "@/components/dashboard/widget-registry";
import { WidgetTail } from "@/components/dashboard/widget-tail";
import { SegmentedToggle } from "@/components/waste/reports/segmented-toggle";
import { TrendArea } from "@/components/waste/reports/trend-area";

import { useTranslation } from "@/i18n/use-translation";
import { currencySymbol } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";
import { useWasteFilters, type DatePreset } from "@/hooks/use-waste-filters";
import { useDashboardLayout } from "@/hooks/use-dashboard-layout";
import { useKitchenDisplay } from "@/hooks/use-kitchen-display";
import { useLens } from "@/hooks/use-lens";
import { useWasteStats } from "@/hooks/use-waste-stats";
import { useLineStats } from "@/hooks/use-line-stats";
import {
  useFoodSpendPeriods,
  sumFoodSpend,
  estimateRangeSpend,
  entrantLabel,
  type FoodSpendPeriod,
  type RangeEstimate,
} from "@/hooks/use-food-spend";
import { useSitesWithActiveMenus, useKitchens } from "@/hooks/use-active-menus";
import { useDevices } from "@/hooks/use-devices";
import { useWasteFoods } from "@/hooks/use-waste-foods";
import { useResolvedConfig, type EntityType } from "@/hooks/use-resolved-config";
import { useClaims } from "@/hooks/use-claims";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useAuthStore } from "@/stores/auth";
import type { ProofFilter } from "@/lib/proof";
import { addWidget, moveWidget, removeWidget, type WidgetId } from "@/lib/dashboard-widgets";
import { isPartOfDay, previousPeriodRange } from "@/lib/kpi";
import { lineStatsWindow } from "@/lib/pulse";
import { weeklySpendFromPeriod } from "@/lib/trend-window";
import { pickDefaultSiteId, sortSitesByName } from "@/lib/sites";
import { zonedDay } from "@/lib/zoned-day";

// The homepage's date window is the current Mon–Sun week (POS2-865). Every
// useWasteFilters instance on this page (here + FilterPopover) must share it.
const HOME_PRESET: DatePreset = "this_week";

// Dashboard filters (POS2-851, deferred from POS2-880): the shared popover minus the
// row-level fields that aren't meaningful over the aggregated dashboard view —
// price / weight / tier / confirmation (kept on Transactions).
const DASHBOARD_FILTER_FIELDS: FilterField[] = [
  "dates",
  "times",
  "site",
  "kitchen",
  "waste_type",
  "sensor",
  "food",
  "service_time",
];

/**
 * What cards 4 and 5 show: the entered sum while entered weeks cover every day of the range
 * (POS2-923's rule — never a derived figure), and the range estimate once days had to be
 * filled at the daily average, which the Est. chip then quantifies (POS2-996).
 */
function rangeFigure(sum: number | null, estimate: RangeEstimate | null): number | null {
  return estimate && estimate.filledDays > 0 ? estimate.total : sum;
}

/** Est.-chip counts, or null when nothing in the range was filled. */
function fillOf(estimate: RangeEstimate | null) {
  if (!estimate || estimate.filledDays === 0) return null;
  return { filledDays: estimate.filledDays, rangeDays: estimate.rangeDays };
}

export default function DashboardPage() {
  const { locale, t } = useTranslation();
  const [lens, setLens] = useLens();
  const kitchen = useKitchenDisplay();
  const sitesQuery = useSitesWithActiveMenus();
  // Viewer id, to decide the provenance chip's "you" vs the entrant's name (POS2-969).
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // Single-site dashboard (POS2-913): default to the first site alphabetically when
  // nothing is selected, so every widget and per-site feature resolves against one
  // concrete site — never an org-wide aggregate. The default is handed to
  // useWasteFilters so its seed writes it in one pass (persisted site wins), rather
  // than a separate write that would race the restore (POS2-852).
  const defaultSiteId = useMemo(() => pickDefaultSiteId(sitesQuery.data ?? []), [sitesQuery.data]);
  const switcherSiteId = useAdminOrgStore((s) => s.siteId);
  const { filters, dateRange, dayRange, defaultRange, timezone, hasActiveFilters, clear } =
    useWasteFilters(HOME_PRESET, defaultSiteId ?? null);
  // The same precedence the seed uses (`use-waste-filters.ts`): the switcher's site was picked
  // deliberately, so it outranks the alphabetical default. Reading only `defaultSiteId` here is
  // what turned "the URL lost its params" into "the dashboard shows another site while the
  // switcher names yours" — the two halves of POS2-1279's item 4 belong together. Org level is
  // "", which is not a site, so `||` is what lets it fall through to the page default.
  const effectiveSiteId = filters.site_id ?? (switcherSiteId || defaultSiteId);
  // Today in the site's zone — the open end of a relative preset, and what an inline
  // food-spend/covers entry dates itself against (POS2-1039).
  const today = useMemo(() => zonedDay(new Date(), timezone), [timezone]);
  // The window the "Explore this" links hand over (POS2-886), as site-local civil days.
  // An open end closes at today; an all-time range has no concrete bounds to hand over,
  // so the affordance is withheld rather than guessing one.
  const exploreWindow = useMemo(
    () => (dayRange.from ? { from: dayRange.from, to: dayRange.to ?? today } : null),
    [dayRange, today],
  );

  // Every widget reads the resolved single site (injected so there's no org-wide
  // flash before the URL write lands). Tier/confirmation pass through from the
  // FilterPopover (POS2-787).
  const resolvedFilters = useMemo(
    () => ({ ...filters, site_id: effectiveSiteId }),
    [filters, effectiveSiteId],
  );
  const statsFilters = useMemo(
    () => ({ ...resolvedFilters, status: "successful" }),
    [resolvedFilters],
  );
  // The dashboard's universe is food-only (POS2-861): every stats query on this
  // page excludes packaging at the RPC, so the KPI row, chart, and heatmap agree.
  // An explicit waste_type filter from the popover suppresses the exclusion.
  const statsQuery = useWasteStats(statsFilters, dateRange, { excludeWasteType: "packaging" });
  // Week-over-week pill (POS2-869): the same-length period shifted back, fetched
  // with the same filters/scope. No backend change — just a second stats call.
  // Week-anchored presets compare against the same weekday slice of last week
  // (7-day shift), not the immediately-preceding block (POS2-956).
  const weekAligned = filters.preset === "this_week" || filters.preset === "last_week";
  const previousRange = useMemo(
    () => previousPeriodRange(dateRange, new Date(), weekAligned),
    [dateRange, weekAligned],
  );
  const previousStatsQuery = useWasteStats(
    statsFilters,
    previousRange ?? { from: null, to: null },
    // Only the totals feed the week-over-week pill, so request just that dim (POS2-960).
    // Sequenced after the main query resolves (POS2-994): two stats scans in flight at
    // once starve the shared instance; the delta pill is non-critical, so it waits.
    {
      enabled: previousRange != null && statsQuery.isSuccess,
      excludeWasteType: "packaging",
      dims: ["total"],
    },
  );
  const previousTotals = previousStatsQuery.data
    ? {
        totalWeight: previousStatsQuery.data.total_weight,
        totalCost: previousStatsQuery.data.total_cost,
      }
    : null;
  const kitchensQuery = useKitchens(effectiveSiteId);
  const devicesQuery = useDevices();
  // All kitchens (org-wide) → annotate each device with its site_id so the filter popover
  // scopes the sensor list to the chosen site, like Explore (POS2-851).
  const allKitchensQuery = useKitchens(null);
  // Defer the org-wide foods scan until the filter popover is first opened (POS2-960).
  const [filtersOpened, setFiltersOpened] = useState(false);
  const foodsQuery = useWasteFoods(filtersOpened);

  // Currency follows the org→site→kitchen config stack, scoped to the active filter
  // (same resolution as Reports / per-event cost — never hardcoded).
  const { orgId: claimsOrgId, isPlatformAdmin } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  const orgId = isPlatformAdmin ? adminOrgId : claimsOrgId;
  const configEntity: { type: EntityType; id: string | null } = filters.kitchen_id
    ? { type: "kitchen", id: filters.kitchen_id }
    : effectiveSiteId
      ? { type: "site", id: effectiveSiteId }
      : { type: "organisation", id: orgId || null };
  const resolvedConfig = useResolvedConfig(configEntity.type, configEntity.id).data?.config;
  const resolvedCurrency = resolvedConfig?.currency?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";
  // Dashboard target (POS2-866): waste % of purchases, resolved on the same
  // org→account→site→kitchen stack — drives the chart's target line (POS2-870).
  const targetPctRaw = resolvedConfig?.waste_pct_target?.value;
  const targetPct =
    typeof targetPctRaw === "number"
      ? targetPctRaw
      : typeof targetPctRaw === "string" &&
          targetPctRaw.trim() !== "" &&
          !Number.isNaN(Number(targetPctRaw))
        ? Number(targetPctRaw)
        : null;
  // Waste-per-cover target (POS2-866/921), same resolution stack — drives card 3's notch.
  const targetPerCoverRaw = resolvedConfig?.waste_per_cover_target_g?.value;
  const wastePerCoverTargetG =
    typeof targetPerCoverRaw === "number"
      ? targetPerCoverRaw
      : typeof targetPerCoverRaw === "string" &&
          targetPerCoverRaw.trim() !== "" &&
          !Number.isNaN(Number(targetPerCoverRaw))
        ? Number(targetPerCoverRaw)
        : null;

  const sites = useMemo(() => sortSitesByName(sitesQuery.data ?? []), [sitesQuery.data]);
  const siteName = sites.find((s) => s.id === effectiveSiteId)?.name ?? null;
  const kitchens = kitchensQuery.data ?? [];
  const devices = useMemo(() => {
    const kitchenSite = new Map((allKitchensQuery.data ?? []).map((k) => [k.id, k.site_id]));
    return (devicesQuery.data ?? []).map((d) => ({
      ...d,
      site_id: kitchenSite.get(d.kitchen_id) ?? null,
    }));
  }, [devicesQuery.data, allKitchensQuery.data]);
  // The Food filter's options are inventory item names, filtered by id — so the label is
  // display-only and goes through `foodLabel` like every other food name (POS2-1134).
  const foods = useMemo(
    () => (foodsQuery.data ?? []).map((f) => ({ value: f.id, label: foodLabel(t, f.label) })),
    [foodsQuery.data, t],
  );

  const stats = statsQuery.data;
  const isEmpty = !statsQuery.isLoading && stats != null && stats.total_events === 0;

  // Food spend (POS2-757): the weekly figures entered in the Food spend dialog for the active
  // site, over the periods overlapping the filter range. Null (→ "N/A") until one exists.
  const [foodSpendOpen, setFoodSpendOpen] = useState(false);
  const foodSpendQuery = useFoodSpendPeriods(effectiveSiteId, dayRange, today);
  // The range figure (POS2-923 + POS2-996): covered days at their week's per-day rate, days
  // no entered week covers at the daily average across the entered weeks. Always the Waste %
  // denominator; the card's own figure only once days had to be filled.
  const foodSpendEstimate = useMemo(
    () => estimateRangeSpend(foodSpendQuery.data, dayRange, today),
    [foodSpendQuery.data, dayRange, today],
  );
  const foodSpend = rangeFigure(sumFoodSpend(foodSpendQuery.data), foodSpendEstimate);
  const wastePctSpend = foodSpendEstimate?.total ?? null;
  const foodSpendPeriods = useMemo(() => foodSpendQuery.data ?? [], [foodSpendQuery.data]);
  const latestFoodSpendPeriod = foodSpendPeriods.reduce<FoodSpendPeriod | null>(
    (latest, p) => (latest == null || p.updated_at > latest.updated_at ? p : latest),
    null,
  );
  // Dates the shown spend covers — the full span across overlapping periods (period_*
  // are ISO date strings, so lexical sort gives min start / max end).
  const foodSpendCoveredRange = foodSpendPeriods.length
    ? {
        start: foodSpendPeriods.map((p) => p.period_start).sort()[0],
        end: foodSpendPeriods.map((p) => p.period_end).sort()[foodSpendPeriods.length - 1],
      }
    : null;

  // Covers (POS2-1259 CP5): the occupancy store, read per day. The whole `use-covers`
  // approximation is gone with it — `estimateRangeCovers` prorated a weekly figure across a
  // sub-range and this endpoint returns the days themselves, so there is nothing left to
  // estimate. Decision 9's granularity rule is applied inside `summary`.
  const [coversOpen, setCoversOpen] = useState(false);
  const coversQuery = useOccupancyRangeCovers(effectiveSiteId, dayRange);
  const coversSummary = coversQuery.summary;
  // The est. chip's counts, or null when the range is fully entered — and null when nothing is
  // entered either, where the card shows "—" and a chip claiming "0 of N filled" would be noise.
  const coversFill = useMemo(() => {
    if (!coversSummary || coversSummary.daysCounted === 0) return null;
    const missing = coversSummary.daysNoData + coversSummary.daysExcludedWeekly;
    return missing === 0
      ? null
      : { filledDays: coversSummary.daysCounted, rangeDays: coversSummary.daysCounted + missing };
  }, [coversSummary]);
  const covers = coversSummary?.covers ?? null;
  // Card 3's denominator and card 5's figure are now the same number — the old split existed
  // only because the estimate and the raw sum disagreed.
  const wastePerCoverCovers = covers;

  // Normalise the period's food spend to a weekly rate for the chart's target line (POS2-870).
  const periodDays = useMemo(() => {
    if (!dateRange.from) return 7;
    const from = new Date(dateRange.from).getTime();
    const to = dateRange.to ? new Date(dateRange.to).getTime() : new Date().getTime();
    return Math.max(1, Math.round((to - from) / 86_400_000));
  }, [dateRange]);
  const weeklySpend = weeklySpendFromPeriod(foodSpend, periodDays);

  // One line-stats call feeds the Pulse strip (POS2-871) and What's-working
  // (POS2-875): n_periods = 4 is What's-working's streak window; Pulse reads
  // the last two buckets by index, so its semantics are unchanged. top_n = 50
  // because the endpoint ranks by current-period cost — lines that declined to
  // a low current cost (the wins) would otherwise fall out. The all-time range
  // has no bounded period → no window → both widgets hide/empty. Scoped with
  // `statsFilters` (status = successful) so counts match the universe The Proof
  // shows — evidence links land on those captures.
  const lineWindow = useMemo(() => lineStatsWindow(dateRange, new Date()), [dateRange]);
  const lineStatsQuery = useLineStats({
    siteId: effectiveSiteId,
    periodStart: lineWindow?.periodStart ?? null,
    periodLengthDays: lineWindow?.periodLengthDays ?? 1,
    nPeriods: 4,
    topN: 50,
    filters: statsFilters,
  });

  // The Proof feed filter (POS2-868): narrows the capture feed to one waste line.
  // Set by the Pulse strip (POS2-871) and heatmap hot cell (POS2-873); for now
  // only cleared from the chip.
  const [proofFilter, setProofFilter] = useState<ProofFilter | null>(null);

  // Customisable tail (POS2-877): the user's saved widget order; every mutation
  // saves optimistically and toasts (copy per the mock).
  const { layout, isLoading: layoutLoading, save: saveLayout } = useDashboardLayout();
  const handleMoveWidget = (id: WidgetId, delta: -1 | 1) =>
    saveLayout(moveWidget(layout, id, delta), t("dashboard.layoutSaved"));
  const handleRemoveWidget = (id: WidgetId) =>
    saveLayout(
      removeWidget(layout, id),
      t("dashboard.widgetRemoved", { widget: t(WIDGET_REGISTRY[id].titleKey) }),
    );
  const handleAddWidget = (id: WidgetId) =>
    saveLayout(addWidget(layout, id), t("dashboard.widgetAdded"));

  return (
    <div className="flex h-full flex-col">
      {/* Topbar: title · period · lens · kitchen display · filters */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200/80 bg-white/90 px-6 py-3 backdrop-blur">
        <h1 className="flex min-w-0 items-baseline gap-1.5 font-display text-xl font-extrabold tracking-tight text-gray-900">
          <span className="shrink-0">{t("nav.dashboard")}</span>
          {siteName && (
            <>
              <span className="shrink-0 text-gray-300">·</span>
              <span className="max-w-[16rem] truncate" title={siteName}>
                {siteName}
              </span>
            </>
          )}
        </h1>
        <div className="flex-1" />
        <PeriodControl defaultPreset={HOME_PRESET} defaultSiteId={defaultSiteId ?? null} />
        <SegmentedToggle
          ariaLabel={t("dashboard.valueLens")}
          value={lens}
          onChange={setLens}
          options={[
            { value: "cost", label: currencySymbol(locale, currency) },
            { value: "weight", label: "kg" },
          ]}
        />
        <Button size="sm" onClick={kitchen.open} data-testid="kitchen-display-button">
          <MonitorPlay className="size-4" aria-hidden />
          {t("dashboard.kitchenLabel")}
        </Button>
        <FilterPopover
          sites={sites}
          kitchens={kitchens}
          devices={devices}
          foods={foods}
          currency={currency}
          defaultPreset={HOME_PRESET}
          defaultSiteId={defaultSiteId ?? null}
          singleSite
          fields={DASHBOARD_FILTER_FIELDS}
          datesDefault={defaultRange}
          onOpenChange={(open) => open && setFiltersOpened(true)}
        />
      </div>

      {/* Body — fixed story order: pulse, KPIs, chart, proof, then the widget tail */}
      <div className="flex-1 overflow-y-auto bg-white p-6">
        <div className="mx-auto flex max-w-[1360px] flex-col gap-3.5">
          {statsQuery.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {t("dashboard.loadFailed")} {String(statsQuery.error)}
            </div>
          )}

          {statsQuery.isLoading && <DashboardSkeleton />}

          {isEmpty && <EmptyState hasFilters={hasActiveFilters} onClear={clear} />}

          {!statsQuery.isLoading && stats != null && !isEmpty && (
            <>
              <PulseStrip
                isLoading={lineStatsQuery.isLoading}
                lines={lineStatsQuery.data?.lines}
                onViewCaptures={(cell) =>
                  setProofFilter({ kind: "line", key: cell.lineKey, label: cell.label })
                }
              />
              <DashboardKpis
                totalWeight={stats.total_weight}
                totalCost={stats.total_cost}
                foodSpend={foodSpend}
                wastePctSpend={wastePctSpend}
                previous={previousTotals}
                targetPct={targetPct}
                currency={currency}
                lens={lens}
                foodSpendEnteredAt={latestFoodSpendPeriod?.updated_at ?? null}
                foodSpendEnteredBy={entrantLabel(latestFoodSpendPeriod, currentUserId)}
                foodSpendCoveredRange={foodSpendCoveredRange}
                foodSpendFill={fillOf(foodSpendEstimate)}
                onOpenFoodSpend={() => setFoodSpendOpen(true)}
                covers={covers}
                wastePerCoverCovers={wastePerCoverCovers}
                wastePerCoverTargetG={wastePerCoverTargetG}
                coversEnteredAt={coversSummary?.enteredAt ?? null}
                coversEnteredBy={entrantLabel(
                  coversSummary
                    ? {
                        entered_by_id: coversSummary.enteredById,
                        entered_by_name: coversSummary.enteredByName,
                      }
                    : null,
                  currentUserId,
                )}
                coversFill={coversFill}
                onOpenCovers={() => setCoversOpen(true)}
                partOfDay={isPartOfDay(filters)}
              />
              <TrendArea
                filters={statsFilters}
                currency={currency}
                timezone={timezone}
                lens={lens}
                targetPct={targetPct}
                weeklySpend={weeklySpend}
              />
              <ProofWidget
                filters={resolvedFilters}
                dateRange={dateRange}
                lens={lens}
                currency={currency}
                filter={proofFilter}
                onClearFilter={() => setProofFilter(null)}
              />
              {/* Customisable tail (positions 5–8, POS2-877): the user's saved
                  widget order through the registry, closed by the add card.
                  Gated on the layout query so a saved order never flashes the
                  default first. */}
              {!layoutLoading && (
                <WidgetTail
                  layout={layout}
                  ctx={{
                    t,
                    stats,
                    lens,
                    currency,
                    lineStatsLoading: lineStatsQuery.isLoading,
                    lines: lineStatsQuery.data?.lines,
                    periodLengthDays: lineWindow?.periodLengthDays ?? 7,
                    onViewLineEvidence: (line) =>
                      setProofFilter({ kind: "line", key: line.lineKey, label: line.label }),
                    onDrillHeatCell: (cell) => setProofFilter({ kind: "cell", ...cell }),
                    exploreWindow,
                    exploreFilters: {
                      site_id: effectiveSiteId,
                      kitchen_id: filters.kitchen_id,
                      device_serial: filters.device_serial,
                      waste_type: filters.waste_type,
                      service_time: filters.service_time,
                      food: filters.food,
                      time_from: filters.time_from,
                      time_to: filters.time_to,
                    },
                  }}
                  onMove={handleMoveWidget}
                  onRemove={handleRemoveWidget}
                  onAdd={handleAddWidget}
                />
              )}
            </>
          )}
        </div>
      </div>

      <FoodSpendDialog
        open={foodSpendOpen}
        onClose={() => setFoodSpendOpen(false)}
        siteId={effectiveSiteId}
        siteName={siteName}
        currency={currency}
        dayRange={dayRange}
        today={today}
      />

      <BaseCoversDialog
        open={coversOpen}
        onClose={() => setCoversOpen(false)}
        siteId={effectiveSiteId}
        siteName={siteName}
        dayRange={dayRange}
        today={today}
      />

      {kitchen.isOpen && (
        <KitchenDisplay
          siteId={effectiveSiteId}
          siteName={sites.find((s) => s.id === effectiveSiteId)?.name ?? ""}
          filters={statsFilters}
          currency={currency}
          timezone={timezone}
          targetPct={targetPct}
          onExit={kitchen.close}
        />
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-3.5" data-testid="dashboard-skeleton">
      <div className="rounded-xl border border-gray-200/80 bg-white p-4">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="mt-3 h-6 w-2/3" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-200/80 bg-white p-4">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="mt-2 h-7 w-1/2" />
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-gray-200/80 bg-white p-4">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="mt-4 h-[240px] w-full" />
      </div>
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-200/80 bg-white p-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="mt-4 h-[160px] w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ hasFilters, onClear }: { hasFilters: boolean; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <BarChart3 className="size-10 text-gray-300" aria-hidden />
      <h3 className="mt-3 text-sm font-medium text-gray-900">
        {hasFilters ? t("dashboard.emptyWithFilters") : t("dashboard.emptyNoData")}
      </h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        {hasFilters ? t("dashboard.emptyWithFiltersHint") : t("dashboard.emptyNoDataHint")}
      </p>
      {hasFilters && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onClear}>
          {t("dashboard.clearFilters")}
        </Button>
      )}
    </div>
  );
}
